import { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Search, Loader2, MapPin, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VENEZUELA_CENTER = [8.0, -66.0];
const DEFAULT_ZOOM = 6;
const PIN_ZOOM = 17;

/**
 * Interactive Leaflet map for picking the precise property location.
 *
 * - Search box (calls /api/geocode/search) shows a result list.
 * - Click a result, drag the pin, or click the map to set/move the pin.
 * - Reports back lat/lng + (optionally) the display address via onChange.
 */
export default function MapAddressPicker({
  latitude = 0,
  longitude = 0,
  city = '',
  defaultQuery = '',   // pre-fill the search box from parent (state/city/neighborhood/address)
  onChange,            // ({lat, lng, address?}) => void
  height = 320,
  testId = 'map-picker',
}) {
  const { language } = useLanguage();
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [query, setQuery] = useState(defaultQuery || '');
  // Track whether the user has manually edited the search box. We only
  // sync from `defaultQuery` (parent address fields) while the user
  // hasn't taken control — once they type or clear, parent updates stop
  // overwriting their input.
  const userEditedRef = useRef(false);
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);

  const tt = (en, es) => (language === 'es' ? es : en);

  const hasPin = latitude && longitude && (latitude !== 0 || longitude !== 0);

  // Mirror parent address composition into the search box until user edits.
  useEffect(() => {
    if (!userEditedRef.current) {
      setQuery(defaultQuery || '');
    }
  }, [defaultQuery]);

  // Init map once
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const initialCenter = hasPin ? [latitude, longitude] : VENEZUELA_CENTER;
    const initialZoom = hasPin ? PIN_ZOOM : DEFAULT_ZOOM;

    mapRef.current = L.map(containerRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      scrollWheelZoom: true,
      zoomControl: true,
    });
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom: 19,
    }).addTo(mapRef.current);

    // Click anywhere on the map to set a pin.
    mapRef.current.on('click', (e) => {
      placePin(e.latlng.lat, e.latlng.lng, true);
    });

    if (hasPin) {
      placePin(latitude, longitude, false);
    }

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        markerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync external lat/lng changes (e.g. parent reset) into the map.
  useEffect(() => {
    if (!mapRef.current) return;
    if (hasPin) {
      placePin(latitude, longitude, false);
      mapRef.current.flyTo([latitude, longitude], PIN_ZOOM, { duration: 0.6 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [latitude, longitude]);

  const placePin = (lat, lng, reportToParent, address) => {
    if (!mapRef.current) return;
    if (!markerRef.current) {
      const icon = L.divIcon({
        className: 'wakacasa-pin',
        html: '<div style="width:28px;height:28px;border-radius:50%;background:#D4AF37;border:3px solid #0B192C;box-shadow:0 4px 12px rgba(0,0,0,.3);display:flex;align-items:center;justify-content:center;color:#0B192C;font-weight:700;font-size:14px;">★</div>',
        iconSize: [28, 28],
        iconAnchor: [14, 14],
      });
      markerRef.current = L.marker([lat, lng], { icon, draggable: true }).addTo(mapRef.current);
      markerRef.current.on('dragend', async (e) => {
        const ll = e.target.getLatLng();
        if (onChange) onChange({ lat: ll.lat, lng: ll.lng });
        try {
          const r = await axios.get(`${API}/geocode/reverse`, { params: { lat: ll.lat, lng: ll.lng } });
          if (r.data?.result?.display_name && onChange) {
            onChange({ lat: ll.lat, lng: ll.lng, address: r.data.result.display_name });
          }
        } catch { /* silent */ }
      });
    } else {
      markerRef.current.setLatLng([lat, lng]);
    }
    if (reportToParent && onChange) {
      onChange({ lat, lng, address });
    }
  };

  const search = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    const q = query.trim();
    if (q.length < 3) {
      toast.error(tt('Type at least 3 characters', 'Escribe al menos 3 caracteres'));
      return;
    }
    setSearching(true);
    setOpen(true);
    try {
      const enriched = city && !q.toLowerCase().includes(city.toLowerCase()) ? `${q}, ${city}` : q;
      const r = await axios.get(`${API}/geocode/search`, { params: { q: enriched, limit: 6 } });
      setResults(r.data?.results || []);
      if ((r.data?.results || []).length === 0) {
        toast.info(tt('No results', 'Sin resultados'));
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || tt('Search failed', 'Error de búsqueda');
      toast.error(typeof detail === 'string' ? detail : tt('Search failed', 'Error de búsqueda'));
    } finally {
      setSearching(false);
    }
  };

  const handleInputKeyDown = (e) => {
    // Prevent Enter from submitting the parent form (e.g. property creation).
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      search();
    }
  };

  const pickResult = (r) => {
    placePin(r.lat, r.lng, true, r.display_name);
    if (mapRef.current) mapRef.current.flyTo([r.lat, r.lng], PIN_ZOOM, { duration: 0.6 });
    setOpen(false);
  };

  const clearPin = () => {
    if (markerRef.current) {
      markerRef.current.remove();
      markerRef.current = null;
    }
    if (onChange) onChange({ lat: 0, lng: 0 });
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => { userEditedRef.current = true; setQuery(e.target.value); }}
            onKeyDown={handleInputKeyDown}
            placeholder={tt(
              'Search address, e.g. Av. San Juan Bosco, Altamira',
              'Buscar dirección, ej. Av. San Juan Bosco, Altamira'
            )}
            className="pl-9 h-10"
            data-testid={`${testId}-search-input`}
          />
        </div>
        <Button
          type="button"
          onClick={(e) => search(e)}
          disabled={searching || query.trim().length < 3}
          className="h-10 px-4 bg-[#0B192C] hover:bg-[#0B192C]/90 text-white gap-1.5"
          data-testid={`${testId}-search-btn`}
        >
          {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          {tt('Search', 'Buscar')}
        </Button>
      </div>

      {open && results.length > 0 && (
        <div className="border border-slate-200 rounded-lg max-h-48 overflow-y-auto bg-white" data-testid={`${testId}-results`}>
          {results.map((r, i) => (
            <button
              key={i}
              type="button"
              onClick={() => pickResult(r)}
              className="w-full text-left px-3 py-2 hover:bg-amber-50 border-b last:border-b-0 text-sm flex items-start gap-2"
              data-testid={`${testId}-result-${i}`}
            >
              <MapPin className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
              <span className="line-clamp-2">{r.display_name}</span>
            </button>
          ))}
        </div>
      )}

      <div className="relative rounded-xl overflow-hidden border border-slate-200">
        <div ref={containerRef} style={{ height: `${height}px`, width: '100%' }} data-testid={`${testId}-map`} />
        {hasPin && (
          <button
            type="button"
            onClick={clearPin}
            title={tt('Clear pin', 'Quitar pin')}
            className="absolute top-2 right-2 z-[400] inline-flex items-center gap-1 bg-white/95 hover:bg-white text-slate-700 hover:text-red-600 px-2 py-1 rounded-md shadow text-xs font-medium border border-slate-200"
            data-testid={`${testId}-clear-pin`}
          >
            <X className="w-3.5 h-3.5" />
            {tt('Clear pin', 'Quitar pin')}
          </button>
        )}
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500">
        <span>
          {tt(
            'Click the map, drag the pin, or use search to set the exact location.',
            'Haz clic en el mapa, arrastra el pin o usa la búsqueda para fijar la ubicación.'
          )}
        </span>
        {hasPin && (
          <span className="font-mono text-[11px]" data-testid={`${testId}-coords`}>
            {Number(latitude).toFixed(5)}, {Number(longitude).toFixed(5)}
          </span>
        )}
      </div>
    </div>
  );
}
